import { useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { motion, useInView } from "framer-motion";
import { Button } from "../ui/button";
import { useLanguage } from "../../contexts/LanguageContext";

export const BeforeAfterSection = () => {
  const { language, direction, translations: t } = useLanguage();
  const ArrowIcon = direction === 'rtl' ? ArrowLeft : ArrowRight;
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const cases = [
    { 
      beforeImage: "https://customer-assets.emergentagent.com/job_c644e515-2e15-42fb-920b-d09434c457a2/artifacts/w8nvezb3_%D7%9C%D7%A4%D7%A0%D7%99.jpeg",
      afterImage: "https://customer-assets.emergentagent.com/job_c644e515-2e15-42fb-920b-d09434c457a2/artifacts/nno1d9jc_%D7%90%D7%97%D7%A8%D7%99.jpeg",
      label: language === 'he' ? 'טיפול בצלקת יד' : 'Hand scar treatment'
    },
    { 
      beforeImage: "https://customer-assets.emergentagent.com/job_e84fca2b-7e76-4834-b103-9bf7c18b7fcd/artifacts/d2klr9zg_%D7%9C%D7%A4%D7%A0%D7%99%20%D7%95%D7%90%D7%97%D7%A8%D7%99%2002.png",
      afterImage: null,
      label: language === 'he' ? 'טיפול בצלקת פנים' : 'Facial scar treatment'
    },
    { 
      beforeImage: "/chin-before.png",
      afterImage: "/chin-after.png",
      label: language === 'he' ? 'טיפול בצלקת פנים — סנטר ושפתיים' : 'Facial scar treatment — chin and lips'
    },
    { 
      beforeImage: "https://customer-assets.emergentagent.com/job_4ee2cf2a-643f-4320-8999-bcdad8b13d97/artifacts/ts65hw1j_02.jpeg",
      afterImage: "https://customer-assets.emergentagent.com/job_4ee2cf2a-643f-4320-8999-bcdad8b13d97/artifacts/i3pd3mco_01.jpeg",
      label: language === 'he' ? 'הסרת שומות' : 'Mole removal'
    },
  ];

  return (
    <section id="results" className="py-20 md:py-28 bg-[#2C2C2C] scroll-mt-20" data-testid="results-section" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          <span className="text-[#C4956A] font-semibold mb-2 block">{t.results.label}</span>
          <h2 className="font-['Heebo'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t.results.title}
          </h2>
          <div className="w-20 h-1 bg-[#C4956A] mx-auto" />
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {cases.map((item, i) => (
            <motion.div
              key={i}
              className="relative rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.15 }}
            >
              <div className="aspect-square relative bg-[#3C3C3C] rounded-2xl overflow-hidden">
                {item.afterImage ? (
                  <div className="w-full h-full flex">
                    <div className="w-1/2 h-full relative">
                      <img src={item.beforeImage} alt={`${language === 'he' ? 'לפני' : 'Before'} - ${item.label}`} className="w-full h-full object-cover object-center" loading="lazy" decoding="async" />
                      <span className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">{language === 'he' ? 'לפני' : 'Before'}</span>
                    </div>
                    <div className="w-1/2 h-full relative">
                      <img src={item.afterImage} alt={`${language === 'he' ? 'אחרי' : 'After'} - ${item.label}`} className="w-full h-full object-cover object-center" loading="lazy" />
                      <span className="absolute top-2 right-2 bg-[#C4956A] text-white text-xs px-2 py-1 rounded">{language === 'he' ? 'אחרי' : 'After'}</span>
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full relative">
                    <img src={item.beforeImage} alt={`${language === 'he' ? 'לפני ואחרי' : 'Before and after'} - ${item.label}`} className="w-full h-full object-cover object-center" loading="lazy" decoding="async" />
                    <span className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">{language === 'he' ? 'לפני' : 'Before'}</span>
                    <span className="absolute top-2 right-2 bg-[#C4956A] text-white text-xs px-2 py-1 rounded">{language === 'he' ? 'אחרי' : 'After'}</span>
                  </div>
                )}
                <div className="absolute bottom-0 inset-x-0 p-4">
                  <span className="text-white font-semibold drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] bg-black/60 px-3 py-1.5 rounded-lg text-sm">{item.label}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p className="text-center mt-8 text-sm text-white/50" initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 0.6 }}>
          {t.results.disclaimer}
        </motion.p>
        <motion.p className="text-center mt-2 text-xs text-white/40" initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 0.7 }}>
          {language === 'he' 
            ? '* הטיפולים המוצגים הם טיפולים פרא-רפואיים ואינם מהווים טיפול רפואי. התוצאות משתנות מאדם לאדם.'
            : '* Treatments shown are paramedical and do not constitute medical treatment. Results vary from person to person.'}
        </motion.p>

        <motion.div className="text-center mt-10" initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.8 }}>
          <a href="#contact">
            <Button className="rounded-full px-8 py-6 bg-[#C4956A] hover:bg-[#b38559] hover:scale-[1.02] text-white text-lg transition-all">
              {t.results.cta} {t.results.ctaButton}
              <ArrowIcon className="w-5 h-5 mx-2" />
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
};
